import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Icon,
  Pagination,
  Popup,
  Table,
  Dropdown,
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { API, showError, showSuccess } from '../helpers';
import { useTranslation } from 'react-i18next';

import { ITEMS_PER_PAGE } from '../constants';
import {
  renderGroup,
  renderNumber,
  renderQuota,
  renderText,
  renderChannelLimit,
} from '../helpers/render';
import { StudioHero, StudioStat } from './StudioPage';

// 统一样式常量（与渠道管理保持一致）—— 深空黑 + 紫蓝霓虹
const styles = {
  page: {
    background: 'transparent',
    borderRadius: 20,
    padding: 20,
  },
  tableHeader: {
    background: '#f5f5f5',
    color: '#18181b',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.2,
    whiteSpace: 'nowrap',
  },
  tableRow: {
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease',
    background: 'transparent',
  },
  tableRowHover: {
    transform: 'translateY(-1px)',
    background: 'linear-gradient(90deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.02) 100%)',
    boxShadow: 'inset 2px 0 0 #18181b, 0 8px 20px rgba(0,0,0,0.06)',
  },
  button: {
    borderRadius: 10,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    transition: 'transform 0.16s ease, box-shadow 0.16s ease',
  },
};

/**
 * 渲染用户角色
 * @param {number} role - 角色码
 * @param {Function} t - 翻译函数
 * @returns {JSX.Element}
 */
function renderRole(role, t) {
  const cfg = {
    1: { text: t('user.table.role_types.normal'), bg: '#f5f5f5', color: '#6b7280', border: 'rgba(0,0,0,0.12)' },
    10: { text: t('user.table.role_types.admin'), bg: '#fffbeb', color: '#d97706', border: 'rgba(217,119,6,0.3)' },
    100: { text: t('user.table.role_types.super_admin'), bg: '#fff7ed', color: '#ea580c', border: 'rgba(234,88,12,0.3)' },
  }[role] || { text: t('user.table.role_types.unknown'), bg: '#fef2f2', color: '#dc2626', border: 'rgba(220,38,38,0.3)' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: 6,
      background: cfg.bg,
      color: cfg.color,
      fontSize: 12,
      fontWeight: 500,
      border: `1px solid ${cfg.border}`,
      whiteSpace: 'nowrap',
    }}>
      {cfg.text}
    </span>
  );
}

const UsersTable = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [orderBy, setOrderBy] = useState('');
  const [hoveredRow, setHoveredRow] = useState(null);

  const loadUsers = async (startIdx) => {
    const res = await API.get(`/api/user/?p=${startIdx}&order=${orderBy}`);
    const { success, message, data } = res.data;
    if (success) {
      if (startIdx === 0) {
        setUsers(data);
      } else {
        let newUsers = users;
        newUsers.push(...data);
        setUsers(newUsers);
      }
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(users.length / ITEMS_PER_PAGE) + 1) {
        // In this case we have to load more data and then append them.
        await loadUsers(activePage - 1, orderBy);
      }
      setActivePage(activePage);
    })();
  };

  useEffect(() => {
    loadUsers(0, orderBy)
      .then()
      .catch((reason) => {
        showError(reason);
      });
  }, [orderBy]);

  const manageUser = (username, action, idx) => {
    (async () => {
      const res = await API.post('/api/user/manage', {
        username,
        action,
      });
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('user.messages.operation_success'));
        let user = res.data.data;
        let newUsers = [...users];
        let realIdx = (activePage - 1) * ITEMS_PER_PAGE + idx;
        if (action === 'delete') {
          newUsers[realIdx].deleted = true;
        } else {
          newUsers[realIdx].status = user.status;
          newUsers[realIdx].role = user.role;
        }
        setUsers(newUsers);
      } else {
        showError(message);
      }
    })();
  };

  /**
   * 渲染用户状态徽标
   * @param {number} status - 状态码
   * @returns {JSX.Element}
   */
  const renderStatus = (status) => {
    const cfg = {
      1: { text: t('user.table.status_types.activated'), bg: '#f0fdf4', color: '#16a34a', border: 'rgba(22,163,74,0.3)', icon: 'check circle' },
      2: { text: t('user.table.status_types.banned'), bg: '#fef2f2', color: '#dc2626', border: 'rgba(220,38,38,0.3)', icon: 'ban' },
    }[status] || { text: t('user.table.status_types.unknown'), bg: '#f5f5f5', color: '#6b7280', border: 'rgba(0,0,0,0.12)', icon: 'question circle' };
    return (
      <span style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: 6,
        background: cfg.bg,
        color: cfg.color,
        fontSize: 12,
        fontWeight: 500,
        border: `1px solid ${cfg.border}`,
        whiteSpace: 'nowrap',
      }}>
        <Icon name={cfg.icon} style={{ margin: 0, fontSize: 11 }} /> {cfg.text}
      </span>
    );
  };

  const searchUsers = async () => {
    if (searchKeyword === '') {
      // if keyword is blank, load files instead.
      await loadUsers(0);
      setActivePage(1);
      setOrderBy('');
      return;
    }
    setSearching(true);
    const res = await API.get(`/api/user/search?keyword=${searchKeyword}`);
    const { success, message, data } = res.data;
    if (success) {
      setUsers(data);
      setActivePage(1);
    } else {
      showError(message);
    }
    setSearching(false);
  };

  const handleKeywordChange = async (e, { value }) => {
    setSearchKeyword(value.trim());
  };

  const sortUser = (key) => {
    if (users.length === 0) return;
    setLoading(true);
    let sortedUsers = [...users];
    sortedUsers.sort((a, b) => {
      if (!isNaN(a[key])) {
        // If the value is numeric, subtract to sort
        return a[key] - b[key];
      } else {
        // If the value is not numeric, sort as strings
        return ('' + a[key]).localeCompare(b[key]);
      }
    });
    if (sortedUsers[0].id === users[0].id) {
      sortedUsers.reverse();
    }
    setUsers(sortedUsers);
    setLoading(false);
  };

  const handleOrderByChange = (e, { value }) => {
    setOrderBy(value);
    setActivePage(1);
  };

  return (
    <div className='studio-table-page' style={styles.page}>
      <StudioHero
        icon='users'
        kicker='AI STUDIO / USERS'
        title={t('user.title')}
        description={t('user.studio.subtitle')}
        className='studio-management-hero'
      />

      <section className='studio-management-overview'>
        <div>
          <span className='studio-section-eyebrow'>USER DIRECTORY</span>
          <h2>{t('user.studio.overview_title')}</h2>
          <p>{t('user.studio.overview_subtitle')}</p>
        </div>
        <div className='studio-management-overview__stats'>
          <StudioStat label={t('user.studio.total')} value={users.length} />
          <StudioStat
            label={t('user.studio.enabled')}
            value={users.filter((user) => user.status === 1).length}
            tone='success'
          />
          <StudioStat
            label={t('user.studio.disabled')}
            value={users.filter((user) => user.status !== 1).length}
            tone='danger'
          />
        </div>
      </section>

      <section className='studio-management-toolbar'>
        <div className='studio-management-toolbar__heading'>
          <div>
            <span className='studio-section-eyebrow'>USER CONTROLS</span>
            <h3>{t('user.studio.search')}</h3>
          </div>
          <Icon name='sliders horizontal' />
        </div>
        <Form onSubmit={searchUsers}>
          <Form.Group className='studio-management-search-row'>
            <Form.Input
              icon='search'
              placeholder={t('user.search')}
              value={searchKeyword}
              loading={searching}
              onChange={handleKeywordChange}
              size='small'
              width={6}
              style={{ borderRadius: 8 }}
            />
            <Form.Button primary size='small' type='submit' style={styles.button}>
              <Icon name='search' /> {t('user.studio.search')}
            </Form.Button>
          </Form.Group>
        </Form>
        <div className='studio-management-toolbar__actions'>
          <Button as={Link} to='/user/add' primary loading={loading} size='mini'>
            <Icon name='plus' /> {t('user.buttons.add')}
          </Button>
          <Dropdown
            placeholder={t('user.table.sort_by')}
            selection
            options={[
              { key: '', text: t('user.table.sort.default'), value: '' },
              { key: 'quota', text: t('user.table.sort.by_quota'), value: 'quota' },
              {
                key: 'used_quota',
                text: t('user.table.sort.by_used_quota'),
                value: 'used_quota',
              },
              {
                key: 'request_count',
                text: t('user.table.sort.by_request_count'),
                value: 'request_count',
              },
            ]}
            value={orderBy}
            onChange={handleOrderByChange}
            style={{ minWidth: 160 }}
          />
        </div>
      </section>

      {/* 用户表格 */}
      <section className='studio-management-table-card'>
        <div className='studio-management-table-card__head'>
          <div>
            <span className='studio-section-eyebrow'>USER INVENTORY</span>
            <h3>{t('user.title')}</h3>
          </div>
          <span className='studio-record-count'>{users.length}</span>
        </div>
        <div className='studio-management-table-scroll'>
          <Table
            basic='very'
            compact
            size='small'
            className='studio-management-table studio-user-table'
            style={{ margin: 0, minWidth: 1100 }}
          >
            <Table.Header>
              <Table.Row>
              <Table.HeaderCell style={styles.tableHeader} onClick={() => sortUser('id')}>
                {t('user.table.id')}
              </Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader} onClick={() => sortUser('username')}>
                {t('user.table.username')}
              </Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader} onClick={() => sortUser('group')}>
                {t('user.table.group')}
              </Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader}>
                可访问渠道
              </Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader} onClick={() => sortUser('quota')}>
                {t('user.table.quota')}
              </Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader} onClick={() => sortUser('role')}>
                {t('user.table.role_text')}
              </Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader} onClick={() => sortUser('status')}>
                {t('user.table.status_text')}
              </Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader}>{t('user.table.actions')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {users
              .slice(
                (activePage - 1) * ITEMS_PER_PAGE,
                activePage * ITEMS_PER_PAGE
              )
              .map((user, idx) => {
                if (user.deleted) return <></>;
                const isHovered = hoveredRow === user.id;
                return (
                  <Table.Row
                    key={user.id}
                    style={{
                      ...styles.tableRow,
                      ...(isHovered ? styles.tableRowHover : {}),
                    }}
                    onMouseEnter={() => setHoveredRow(user.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <Table.Cell style={{ padding: '18px 14px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: 999,
                        background: '#f5f5f5',
                        color: '#6b7280',
                        fontSize: 12,
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                      }}>#{user.id}</span>
                    </Table.Cell>
                    <Table.Cell style={{ padding: '18px 14px' }}>
                      <Popup
                        content={user.email ? user.email : '未绑定邮箱地址'}
                        key={user.username}
                        header={user.display_name ? user.display_name : user.username}
                        trigger={
                          <span style={{
                            fontWeight: 800,
                            color: '#18181b',
                            fontSize: 15,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 150,
                            display: 'inline-block',
                            cursor: 'pointer',
                          }}>
                            {renderText(user.username, 15)}
                          </span>
                        }
                        hoverable
                      />
                    </Table.Cell>
                    <Table.Cell style={{ padding: '18px 14px' }}>
                      {renderGroup(user.group)}
                    </Table.Cell>
                    <Table.Cell style={{ padding: '18px 14px' }}>
                      {renderChannelLimit(user.channel_ids, user.channel_names)}
                    </Table.Cell>
                    <Table.Cell style={{ padding: '18px 14px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <Popup
                          content={t('user.table.remaining_quota')}
                          trigger={
                            <span style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: '#16a34a',
                              fontVariantNumeric: 'tabular-nums',
                              cursor: 'help',
                            }}>
                              {renderQuota(user.quota, t)}
                            </span>
                          }
                        />
                        <Popup
                          content={t('user.table.used_quota')}
                          trigger={
                            <span style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: '#18181b',
                              fontVariantNumeric: 'tabular-nums',
                              cursor: 'help',
                            }}>
                              {renderQuota(user.used_quota, t)}
                            </span>
                          }
                        />
                        <Popup
                          content={t('user.table.request_count')}
                          trigger={
                            <span style={{
                              fontSize: 11,
                              color: '#6b7280',
                              cursor: 'help',
                            }}>
                              {renderNumber(user.request_count)} 次请求
                            </span>
                          }
                        />
                      </div>
                    </Table.Cell>
                    <Table.Cell style={{ padding: '18px 14px' }}>{renderRole(user.role, t)}</Table.Cell>
                    <Table.Cell style={{ padding: '18px 14px' }}>{renderStatus(user.status)}</Table.Cell>
                    <Table.Cell style={{ padding: '18px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Button
                          size='mini'
                          color='teal'
                          onClick={() => {
                            manageUser(user.username, 'promote', idx);
                          }}
                          disabled={user.role === 100}
                          style={styles.button}
                        >
                          <Icon name='arrow up' /> {t('user.buttons.promote')}
                        </Button>
                        <Button
                          size='mini'
                          color='orange'
                          onClick={() => {
                            manageUser(user.username, 'demote', idx);
                          }}
                          disabled={user.role === 100}
                          style={styles.button}
                        >
                          <Icon name='arrow down' /> {t('user.buttons.demote')}
                        </Button>
                        <Popup
                          trigger={
                            <Button size='mini' basic negative style={styles.button} disabled={user.role === 100}>
                              <Icon name='trash' /> {t('user.buttons.delete')}
                            </Button>
                          }
                          on='click'
                          flowing
                          hoverable
                        >
                          <Button
                            negative
                            size='mini'
                            onClick={() => {
                              manageUser(user.username, 'delete', idx);
                            }}
                            style={styles.button}
                          >
                            <Icon name='exclamation triangle' /> {t('user.buttons.delete_user')} {user.username}
                          </Button>
                        </Popup>
                        <Button
                          size='mini'
                          basic
                          color={user.status === 1 ? 'red' : 'green'}
                          onClick={() => {
                            manageUser(
                              user.username,
                              user.status === 1 ? 'disable' : 'enable',
                              idx
                            );
                          }}
                          disabled={user.role === 100}
                          style={styles.button}
                        >
                          <Icon name={user.status === 1 ? 'ban' : 'check'} />
                          {user.status === 1
                            ? t('user.buttons.disable')
                            : t('user.buttons.enable')}
                        </Button>
                        <Button
                          size='mini'
                          as={Link}
                          to={'/user/edit/' + user.id}
                          primary
                          style={styles.button}
                        >
                          <Icon name='edit' /> {t('user.buttons.edit')}
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
          </Table.Body>

            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan='8'>
                  <div className='studio-management-table-footer'>
                    <span>{users.length}</span>
                    <Pagination
                      activePage={activePage}
                      onPageChange={onPaginationChange}
                      size='mini'
                      siblingRange={1}
                      totalPages={
                        Math.ceil(users.length / ITEMS_PER_PAGE) +
                        (users.length % ITEMS_PER_PAGE === 0 ? 1 : 0)
                      }
                    />
                  </div>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          </Table>
        </div>
      </section>
    </div>
  );
};

export default UsersTable;
